<template>
  <div class="page-container-wrapper">
    <loader v-if="dataLoading"/>
    <template v-else>
      <div class="page-container-header d-flex align-items-center">
        <h2 class="text-md mb-0">{{ $t('message.Guideline.publications') }}</h2>

        <b-button variant="info" class="mr-2 ml-auto" @click="goBack">{{ $t('message.return') }}</b-button>
      </div>

      <div class="container-fluid">
        <div class="row">
          <!-- LEFT -->
          <div class="col-md-12 col-xl-7">
            <div class="page-container guidelines-editor" id="page-container">
              <b-alert v-show="dataError" class="mt-4" show variant="danger">{{ $t('message.connectionError') }}</b-alert>
              <div class="padding">

                <template v-if="isGuidelineSubmitter && guideline.phase === guidelinePhases.uploadReviewed">
                  <b-card :title="$t('message.Guideline.guidelineWasReviewed')" class="mb-2">
                    <router-link class="btn btn-primary btn text-center" :to="'/guidelines/' + guideline.id + '/upload'">
                      {{ $t('message.Guideline.uploadCorrectedFiles') }}
                    </router-link>
                  </b-card>
                </template>

                <div class="row">
                  <div class="col-md-12">
                    <!-- LEFT BASE DATA -->
                    <base-data-left :guideline="guideline" class="d-xl-none"></base-data-left>

                    <!-- LEFT PROGRESS -->
                    <phase-preview-left :guideline="guideline" class="d-xl-none"></phase-preview-left>

                    <b-card :title="$t('message.Guideline.sectionUploadedFiles')" class="mb-2 fileUploader">
                      <template v-if="filesUploaded && filesUploaded.length">
                        <div class="table-responsive">
                        <b-table striped hover :items="filesUploaded" :fields="['size', 'upload_id', 'name', 'type', 'actions']">
                          <template slot="info" slot-scope="data">
                      <pre>
                        {{ data.item.response.data.id }}
                      </pre>
                          </template>

                          <template slot="upload_id" slot-scope="data">
                            {{ $t("message.Guideline.fileVersion", {version: data.item.upload_id}) }}
                          </template>

                          <template slot="progress" slot-scope="data">
                            <b-progress :value="parseInt(data.item.progress)" :max="100" show-progress animated></b-progress>
                          </template>

                          <template slot="actions" slot-scope="data">
                            <a target="_blank" :href="data.item.url">
                              <font-awesome-icon icon="download" size="lg" class="mr-2"/>
                            </a>

                            <a v-if="isSuperUser" @click.prevent="deleteFileInfo(data.item)">
                              <font-awesome-icon icon="trash" size="lg" class="mr-2 text-danger"/>
                            </a>
                          </template>
                        </b-table>
                        </div>
                      </template>

                      <button class="btn btn-info" @click="prepareZippedFiles(guideline.submitter_id, '')">
                        {{ $t('message.Guideline.downloadZipped') }}
                      </button>
                    </b-card>
                  </div>
                </div>

                <b-card :title="$t('message.Guideline.cdsExport')" class="mb-1">
                  <p class="card-text">
                    <b-button variant="primary" @click="cdsDownload">CDS Download</b-button>
                  </p>
                  <b-alert variant="info" show v-html="$t('message.Guideline.cdsExportInfo')"></b-alert>
                </b-card>

                <div class="row">
                  <div class="col-md-12">
                    <b-card :title="$t('message.Guideline.acceptCheckboxes')" class="mb-2">
                      <b-row>
                        <b-form-checkbox id="checkbox1"
                                         v-model="checkbox1"
                                         :state="errors.first('checkbox1')?'invalid':'success'"
                                         data-vv-name="checkbox1"
                                         v-validate="{ required: true, min_value: 1 }"
                                         value="1"
                                         unchecked-value="0"
                                         class="ml-3">
                          {{ $t('message.Guideline.boardProcessCheckbox1') }}
                        </b-form-checkbox>
                      </b-row>

                      <b-row>
                        <b-form-checkbox id="checkbox2"
                                         v-model="checkbox2"
                                         :state="errors.first('checkbox2')?'invalid':'success'"
                                         v-validate="{ required: true, min_value: 1}"
                                         data-vv-name="checkbox2"
                                         value="1"
                                         unchecked-value="0"
                                         class="ml-3">
                          {{ $t('message.Guideline.boardProcessCheckbox2') }}
                        </b-form-checkbox>
                      </b-row>

                      <b-row>
                        <b-form-checkbox id="checkbox3"
                                         v-model="checkbox3"
                                         :state="errors.first('checkbox3')?'invalid':'success'"
                                         v-validate="{ required: true, min_value: 1 }"
                                         data-vv-name="checkbox3"
                                         value="1"
                                         unchecked-value="0"
                                         class="ml-3">
                          {{ $t('message.Guideline.boardProcessCheckbox3') }}
                        </b-form-checkbox>
                      </b-row>

                      <b-row>
                        <b-form-checkbox id="checkbox4"
                                         v-model="checkbox4"
                                         :state="errors.first('checkbox4')?'invalid':'success'"
                                         v-validate="{ required: true, min_value: 1 }"
                                         data-vv-name="checkbox4"
                                         value="1"
                                         unchecked-value="0"
                                         class="ml-3">
                          {{ $t('message.Guideline.boardProcessCheckbox4') }}
                        </b-form-checkbox>
                      </b-row>

                      <b-row>
                        <b-form-checkbox id="checkbox5"
                                         v-model="checkbox5"
                                         :state="errors.first('checkbox5')?'invalid':'success'"
                                         v-validate="{ required: true, min_value: 1 }"
                                         data-vv-name="checkbox5"
                                         value="1"
                                         unchecked-value="0"
                                         class="ml-3">
                          {{ $t('message.Guideline.boardProcessCheckbox5') }}
                        </b-form-checkbox>
                      </b-row>
                    </b-card>

                    <b-card :title="$t('message.Guideline.fileFormatExamples')" class="mb-1">
                      <p class="card-text">
                        <a target="_blank" href="/pdf/2026_EANM_GL_Standard_Texts_v2.pdf">EANM_GL_Preamble_Acknowledgment.pdf</a>
                      </p>
                    </b-card>

                    <b-card :title="$t('message.Guideline.finalUpload')" class="mb-2">
                      <div class="dropzone mb-4" v-show="$refs.upload && $refs.upload.drop">
                        <div :class="{ 'dragActive' : $refs.upload && $refs.upload.dropActive }">
                          <h5 v-html="$t('message.Guideline.draganddrop')"></h5>
                          <label for="gl" class="btn btn-primary">{{ $t('message.Guideline.selectFiles') }}</label>
                        </div>
                      </div>

                      <template v-if="files && files.length">
                        <div class="table-responsive">
                          <b-table striped hover :items="files" :fields="['size', 'name', 'type', 'progress', 'actions']">
                            <template slot="info" slot-scope="data">
                            <pre>
                              {{ data.item.response.data.id }}
                            </pre>
                            </template>

                            <template slot="progress" slot-scope="data">
                              <b-progress :value="parseInt(data.item.progress)" :max="100" show-progress animated></b-progress>
                            </template>

                            <template slot="actions" slot-scope="data">
                              <a target="_blank" :href="getFileUrl(data.item)">
                                <font-awesome-icon icon="download" size="lg" class="mr-2"/>
                              </a>

                              <a @click.prevent="deleteFileInfo(data.item, data.index)">
                                <font-awesome-icon icon="trash" size="lg" class="mr-2 text-danger"/>
                              </a>
                            </template>
                          </b-table>
                        </div>
                      </template>

                      <file-upload
                          ref="upload"
                          v-model="files"
                          :post-action="uploadUrl"
                          name="gl"
                          :headers="{'Authorization': 'Bearer ' + getAuthToken}"
                          chunk-enabled
                          :chunk="chunk"
                          :drop="true"
                          @input-filter="inputFilter"
                          @input="$refs.upload.active = true"
                          :multiple="true"
                      >
                      </file-upload>
                    </b-card>


                    <b-card :title="$t('message.Guideline.submitSection')" class="mb-2">
                      <b-button :disabled="files.length == 0" variant="primary" @click="finalSubmitInfo"> {{ $t('message.Guideline.submitLabel') }}</b-button>
                    </b-card>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- RIGHT -->
          <div class="d-md-none d-xl-block col-xl-5">
            <base-data-right :guideline="guideline"></base-data-right>

            <phase-preview :guideline="guideline"></phase-preview>
          </div>
        </div>
      </div>

    </template>
  </div>
</template>

<script>
import {
  GUIDELINE_REQUEST_BY_ID,
  GUIDELINE_DELETE_FILE_BY_ID,
  GUIDELINE_CHANGE_PHASE,
} from '@/store/actions/guidelines';

import {Guideline, GuidelinePhases} from '@/lib/guideline.js';

import Api from '@/services/Api';

import FileUpload from 'vue-upload-component/dist/vue-upload-component.part.js'

import BaseDataRight from '@/components/Guideline/BaseData/right.vue'
import BaseDataLeft from '@/components/Guideline/BaseData/left.vue'

import PhasePreview from '@/components/Guideline/PhasePreview'
import PhasePreviewLeft from '@/components/Guideline/PhasePreview/left.vue'

export default {
  name: 'guideline-GPC-final-approval-process',
  components: {
    FileUpload,
    BaseDataRight,
    BaseDataLeft,
    PhasePreview,
    PhasePreviewLeft,
  },
  props: [],
  data() {
    return {
      guideline: new Guideline,
      guidelinePhases: GuidelinePhases,

      files: [],

      checkbox1: 0,
      checkbox2: 0,
      checkbox3: 0,
      checkbox4: 0,
      checkbox5: 0,
    }
  },
  computed: {
    chunk() {
      return {
        headers: {'Authorization': 'Bearer ' + this.getAuthToken},
        action: this.uploadChunkedUrl,
        maxActive: 1,
        maxRetries: 5,
      }
    },

    isGuidelineSubmitter() {
      return this.getUserData.id == this.guideline.submitter_id;
    },

    filesUploaded() {
      if (this.guideline && this.guideline.files) {
        const filtered = this.guideline.files.filter(elem => {
          return elem.upload_id != null && elem.user_id == this.guideline.submitter_id;
        });
        return filtered;
      }

      return [];
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    getData() {
      this.$store.dispatch(GUIDELINE_REQUEST_BY_ID, this.$route.params.id).then((response) => {
        if (response.data.data.phase != this.guidelinePhases.reviewByNationalDelegatesEnd) {
          this.$router.push('/guidelines/' + response.data.data.id + '/view')
        }

        this.guideline = response.data.data;

        this.files = this.guideline.files.filter(elem => (elem.user_id == this.getUserData.id && elem.upload_id == null));

      }).catch((error) => {
        this.handleUnauth(error.response.status);
      });
    },

    goBack() {
      this.$router.push('/guidelines/')
    },

    cdsDownload() {
      Api().get('/cds/' + this.guideline.id)
          .then(response => {
            window.open(response.data.redirectUrl);
          })
          .catch(error => {
            this.handleUnauth(error.response.status);
            this.handleForbidden(error.response.status);
          });
    },

    prepareZippedFiles(userId, uploadId) {
      /**
       * Set as downloaded for user
       */
      let files = this.guideline.files.filter(row => {
        return row.user_id == userId && row.upload_id == uploadId
      })

      for (let x in files) {
        files[x].downloaded = true
      }

      Api().get(`/guidelines/prepare-zipped/${this.guideline.id}/${userId}/${uploadId}`).then(response => {
        window.open(response.data.fileUrl);
      }).catch((error) => {
        this.handleUnauth(error.response.status);
        this.handleForbidden(error.response.status);
      });
    },

    getZipped() {
      window.open(this.guideline.zipUrl);
    },

    getFileUrl(file) {
      return (file.response && file.response.data != undefined) ? file.response.data.url : (file.url != undefined ? file.url : null);
    },

    deleteFileInfo(file, fileIndex) {
      let fileId = file.response != undefined ? file.response.data.id : file.id;

      this.$swal({
        title: this.$t('message.areYouSure'),
        html: this.$t('message.Guideline.deleteFileInfo'),
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('message.yesImSure'),
        cancelButtonText: this.$t('message.noCancel'),
      }).then((result) => {
        if (result.value != undefined) {
          this.deleteFile(fileId, fileIndex);
        }
      });
    },

    deleteFile(fileId) {
      this.$store.dispatch(GUIDELINE_DELETE_FILE_BY_ID, {gid: this.guideline.id, fid: fileId})
          .then(() => {
            this.getData();
          }).catch((error) => {
        this.handleUnauth(error.response.status);
      });
    },

    finalSubmitInfo() {
      this.$validator.validate().then(valid => {
        if (valid) {
          this.$swal({
            title: this.$t('message.areYouSure'),
            html: this.$t('message.Guideline.finalGpcSubmitInfo'),
            type: 'warning',
            showCancelButton: true,
            confirmButtonText: this.$t('message.yesImSure'),
            cancelButtonText: this.$t('message.noCancel'),
          }).then((result) => {
            if (result.value != undefined) {
              this.finalSubmit();
            }
          });
        } else {
          this.$swal({
            title: this.$t('message.error'),
            html: this.$t('message.fillRequiredFields'),
            type: 'error',
            showCancelButton: false
          })
        }
      });

    },

    finalSubmit() {
      this.$store.dispatch(GUIDELINE_CHANGE_PHASE, {id: this.guideline.id, phase: this.guidelinePhases.gpcFinalApproval})
          .then(() => {
            this.$swal({
              title: this.$t("message.success"),
              text: this.$t("message.Guideline.successfullyUploaded"),
              type: 'success',
              confirmButtonText: 'OK'
            });
            this.getData();
          }).catch((error) => {
        this.handleUnauth(error.response.status);
        this.handleForbidden(error.response.status);
      });
    },
  }
}
</script>

<style scoped lang="scss">
</style>

